import React, { useState, useEffect } from 'react';
import { ToggleLeft, ToggleRight, Plus, Search, Settings } from 'lucide-react';
import { superAdminService } from '../../../services/superAdminService';

const FeatureFlags = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFeature, setNewFeature] = useState({
    name: '',
    description: '',
    flag_type: 'boolean',
    default_value: 'false'
  });

  useEffect(() => {
    fetchFeatureFlags();
  }, []);

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      const data = await superAdminService?.getFeatureFlags();
      setFeatures(data || []);
      setError(null);
    } catch (error) {
      setError('Failed to load feature flags');
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredFeatures = features?.filter(feature =>
    feature?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    feature?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  ) || [];

  const handleToggleFeature = async (featureId, currentStatus) => {
    try {
      await superAdminService?.toggleFeatureFlag(featureId, !currentStatus);
      await fetchFeatureFlags();
    } catch (error) {
      setError('Failed to toggle feature flag');
    }
  };

  const handleCreateFeature = async (e) => {
    e?.preventDefault();
    try {
      await superAdminService?.createFeatureFlag(newFeature);
      setShowCreateModal(false);
      setNewFeature({ name: '', description: '', flag_type: 'boolean', default_value: 'false' });
      await fetchFeatureFlags();
    } catch (error) {
      setError('Failed to create feature flag');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'boolean':
        return 'bg-blue-100 text-blue-800';
      case 'string':
        return 'bg-green-100 text-green-800';
      case 'number':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Feature Flags</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Feature Flag
        </button>
      </div>
      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search feature flags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      {/* Feature Flags List */}
      <div className="space-y-4">
        {filteredFeatures?.length === 0 ? (
          <div className="text-center py-12">
            <Settings className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No feature flags found</p>
          </div>
        ) : (
          filteredFeatures?.map((feature) => (
            <div key={feature?.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {feature?.name || 'Unnamed Feature'}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(feature?.flag_type)}`}>
                      {feature?.flag_type || 'boolean'}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">
                    {feature?.description || 'No description available'}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Default: <code className="bg-gray-100 px-1 py-0.5 rounded">{feature?.default_value || 'false'}</code></span>
                    <span>Created: {feature?.created_at ? new Date(feature.created_at)?.toLocaleDateString() : 'Unknown'}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggleFeature(feature?.id, feature?.is_active)}
                    className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                      feature?.is_active
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {feature?.is_active ? (
                      <>
                        <ToggleRight className="w-5 h-5 mr-1" />
                        Active
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5 mr-1" />
                        Inactive
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Create Feature Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Feature Flag</h3>
            
            <form onSubmit={handleCreateFeature}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newFeature?.name}
                  onChange={(e) => setNewFeature({ ...newFeature, name: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., advanced_analytics"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newFeature?.description}
                  onChange={(e) => setNewFeature({ ...newFeature, description: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Describe what this feature flag controls"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={newFeature?.flag_type}
                  onChange={(e) => setNewFeature({ ...newFeature, flag_type: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="boolean">Boolean</option>
                  <option value="string">String</option>
                  <option value="number">Number</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Value
                </label>
                <input
                  type="text"
                  value={newFeature?.default_value}
                  onChange={(e) => setNewFeature({ ...newFeature, default_value: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., false, 0, disabled"
                  required
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Feature Flag
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureFlags;